import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Grid,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  DoneAll as BulkIcon,
  SwapHoriz as ChangeIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';

export default function AuditLog() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');

  useEffect(() => {
    if (profile?.rol === 'coordonator') {
      loadAuditLogs();
    }
  }, [profile]);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, filterAction, filterEntity]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(TABLES.audit_log)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.user_name?.toLowerCase().includes(search) ||
        log.user_email?.toLowerCase().includes(search) ||
        log.entity_name?.toLowerCase().includes(search) ||
        log.action_description?.toLowerCase().includes(search)
      );
    }

    if (filterAction !== 'all') {
      filtered = filtered.filter(log => log.action_type === filterAction);
    }

    if (filterEntity !== 'all') {
      filtered = filtered.filter(log => log.entity_type === filterEntity);
    }

    setFilteredLogs(filtered);
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'APPROVE': return <ApproveIcon sx={{ color: 'success.main' }} />;
      case 'REJECT': return <RejectIcon sx={{ color: 'error.main' }} />;
      case 'BULK_APPROVE': return <BulkIcon sx={{ color: 'info.main' }} />;
      case 'UPDATE': return <ChangeIcon sx={{ color: 'warning.main' }} />;
      default: return <HistoryIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'APPROVE': return 'success';
      case 'REJECT': return 'error';
      case 'BULK_APPROVE': return 'info';
      case 'UPDATE': return 'warning';
      case 'CREATE': return 'primary';
      case 'DELETE': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const actionTypes = [...new Set(logs.map(l => l.action_type).filter(Boolean))];
  const entityTypes = [...new Set(logs.map(l => l.entity_type).filter(Boolean))];

  if (profile?.rol !== 'coordonator') {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Acces restricționat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Doar coordonatorii pot vizualiza jurnalul de audit.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 2, fontSize: 40 }} />
          Jurnal de Audit
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Istoric complet al acțiunilor din sistem
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Caută după utilizator, email, entitate..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Acțiune</InputLabel>
                <Select
                  value={filterAction}
                  label="Acțiune"
                  onChange={(e) => setFilterAction(e.target.value)}
                >
                  <MenuItem value="all">Toate</MenuItem>
                  {actionTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Entitate</InputLabel>
                <Select
                  value={filterEntity}
                  label="Entitate"
                  onChange={(e) => setFilterEntity(e.target.value)}
                >
                  <MenuItem value="all">Toate</MenuItem>
                  {entityTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAuditLogs}
              >
                Reîncarcă
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary" textAlign="right">
                {filteredLogs.length} / {logs.length} înregistrări
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Logs Table */}
      {filteredLogs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {logs.length === 0 ? 'Nu există înregistrări în jurnal' : 'Nu s-au găsit rezultate'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {logs.length === 0 
                ? 'Acțiunile viitoare vor fi înregistrate automat aici.'
                : 'Încearcă să modifici criteriile de căutare.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell width={50}></TableCell>
                <TableCell><strong>Data/Ora</strong></TableCell>
                <TableCell><strong>Utilizator</strong></TableCell>
                <TableCell><strong>Acțiune</strong></TableCell>
                <TableCell><strong>Descriere</strong></TableCell>
                <TableCell><strong>Entitate</strong></TableCell>
                <TableCell align="center"><strong>Detalii</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{getActionIcon(log.action_type)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(log.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {log.user_name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {log.user_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action_type}
                      color={getActionColor(log.action_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.action_description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.entity_type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {log.entity_name || `ID: ${log.entity_id}`}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {(log.old_values || log.new_values || log.metadata) && (
                      <Tooltip 
                        title={
                          <Box sx={{ p: 1 }}>
                            {log.old_values && (
                              <Typography variant="caption" display="block">
                                <strong>Înainte:</strong> {JSON.stringify(log.old_values)}
                              </Typography>
                            )}
                            {log.new_values && (
                              <Typography variant="caption" display="block">
                                <strong>După:</strong> {JSON.stringify(log.new_values)}
                              </Typography>
                            )}
                            {log.metadata && (
                              <Typography variant="caption" display="block">
                                <strong>Metadata:</strong> {JSON.stringify(log.metadata)}
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
